import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

export function getFirstName(displayName?: string | null): string {
  const normalizedName = displayName?.trim();

  if (!normalizedName) {
    return '';
  }

  const orderedName = normalizedName.includes(',')
    ? normalizedName
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .reverse()
        .join(' ')
    : normalizedName;

  return orderedName.split(/\s+/)[0] ?? '';
}

interface SuiteHeroProps {
  visibleToolCount: number;
}

export function SuiteHero({ visibleToolCount }: SuiteHeroProps) {
  const { currentUser } = useOrgMemberStore();
  const { activeWorkspace } = useWorkspaceStore();

  const userName = getFirstName(currentUser?.displayName);
  const greeting = getGreeting();
  const roleBadge = currentUser?.role ?? null;

  return (
    <div className="space-y-3" data-testid="suite-hero">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {greeting}{userName ? `, ${userName}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground">
            {activeWorkspace
              ? `${activeWorkspace.name} · ${activeWorkspace.domain?.label ?? 'Keine Domain'}`
              : 'Kein Workspace aktiv'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {roleBadge && (
            <Badge variant="outline" className="text-xs uppercase tracking-wide" data-testid="role-badge">
              {roleBadge}
            </Badge>
          )}
          <span className="text-sm text-muted-foreground" data-testid="tool-count">
            {visibleToolCount} {visibleToolCount === 1 ? 'Tool' : 'Tools'} verfügbar
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="default" size="sm">
          <Link to="/app/plugins/marketplace">
            <Store className="mr-2 h-4 w-4" />
            Marketplace
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to="/app/workspaces">Workspace verwalten</Link>
        </Button>
      </div>
    </div>
  );
}
